/**
 * Estado de conexao — `PWA §6.3`.
 *
 * > "`navigator.onLine` e notoriamente mentiroso (retorna `true` em Wi-Fi de captive portal e
 * > em 4G sem rota)."
 *
 * A politica, transcrita:
 *
 * - o estado deriva de **falhas reais de requisicao**: 2 falhas de rede consecutivas em <= 10 s
 *   entram em `OFFLINE`;
 * - `navigator.onLine === false` e usado **so como atalho** para entrar mais cedo em `OFFLINE`,
 *   nunca para sair;
 * - a sonda de recuperacao e `HEAD /v1/health` com *backoff* 5 s -> 15 s -> 30 s -> 60 s (teto),
 *   e **so roda enquanto o estado for `OFFLINE`** — polling permanente queima bateria e
 *   franquia;
 * - `DEGRADED` e outra coisa: esta online, mas em `2g`/`slow-2g` ou com `saveData`. Liga o modo
 *   economia (`PWA §11.5`) sem alarmar ninguem.
 *
 * Tudo aqui e puro e recebe o relogio por parametro; o efeito (timer, `fetch`, listeners) mora
 * em `useConexao`.
 */

export type EstadoDeConexao = 'ONLINE' | 'DEGRADED' | 'OFFLINE';

/** `PWA §6.3` — duas falhas consecutivas dentro desta janela. */
export const JANELA_DE_FALHAS_MS = 10_000;
export const FALHAS_PARA_OFFLINE = 2;

/** `PWA §6.3` — 5 s, 15 s, 30 s, 60 s (teto). */
export const BACKOFF_DA_SONDA_MS: readonly number[] = [5_000, 15_000, 30_000, 60_000];

export function esperaDaSonda(tentativa: number): number {
  const indice = Math.min(Math.max(tentativa, 0), BACKOFF_DA_SONDA_MS.length - 1);
  return BACKOFF_DA_SONDA_MS[indice] ?? 60_000;
}

/** O que o navegador informa sobre a rede. Entra por parametro para o teste poder mentir. */
export interface DicasDeRede {
  /** `navigator.onLine`. So serve de atalho para OFFLINE. */
  readonly online: boolean;
  /** `navigator.connection?.effectiveType`. */
  readonly effectiveType?: string | undefined;
  /** `navigator.connection?.saveData`. */
  readonly saveData?: boolean | undefined;
}

export interface EstadoInterno {
  readonly estado: EstadoDeConexao;
  /** Instantes (ms) das falhas de rede recentes, mais antiga primeiro. */
  readonly falhas: readonly number[];
}

export const ESTADO_INICIAL: EstadoInterno = { estado: 'ONLINE', falhas: [] };

/** `PWA §11.5` — o modo economia liga sozinho aqui, com aviso e reversao em um toque na UI. */
export function ehRedeDegradada(dicas: DicasDeRede): boolean {
  if (dicas.saveData === true) return true;
  return dicas.effectiveType === '2g' || dicas.effectiveType === 'slow-2g';
}

export type Evento =
  | { readonly tipo: 'FALHA_DE_REDE'; readonly emMs: number }
  | { readonly tipo: 'SUCESSO_DE_REDE' }
  /** Resultado da sonda `HEAD /v1/health`. Unico caminho de saida de `OFFLINE`. */
  | { readonly tipo: 'SONDA'; readonly ok: boolean }
  | { readonly tipo: 'NAVEGADOR_OFFLINE' }
  | { readonly tipo: 'DICAS_MUDARAM'; readonly dicas: DicasDeRede };

export function reduzir(atual: EstadoInterno, evento: Evento, dicas: DicasDeRede): EstadoInterno {
  switch (evento.tipo) {
    case 'FALHA_DE_REDE': {
      const recentes = [...atual.falhas, evento.emMs].filter(
        (t) => evento.emMs - t <= JANELA_DE_FALHAS_MS,
      );
      if (recentes.length >= FALHAS_PARA_OFFLINE) return { estado: 'OFFLINE', falhas: [] };
      return { estado: atual.estado, falhas: recentes };
    }

    case 'SUCESSO_DE_REDE': {
      // Uma requisicao bem-sucedida **nao** tira de OFFLINE por si so — a saida e sempre pela
      // sonda, que e a unica que verifica o caminho ate a API. Mas ela zera o contador: duas
      // falhas separadas por um sucesso nao sao "consecutivas".
      if (atual.estado === 'OFFLINE') return atual;
      return { estado: ehRedeDegradada(dicas) ? 'DEGRADED' : 'ONLINE', falhas: [] };
    }

    case 'SONDA': {
      if (!evento.ok) return { estado: 'OFFLINE', falhas: [] };
      return { estado: ehRedeDegradada(dicas) ? 'DEGRADED' : 'ONLINE', falhas: [] };
    }

    case 'NAVEGADOR_OFFLINE':
      // Atalho para ENTRAR. `navigator.onLine === true` nunca tira de OFFLINE (nao ha caso
      // 'NAVEGADOR_ONLINE' de proposito).
      return { estado: 'OFFLINE', falhas: [] };

    case 'DICAS_MUDARAM': {
      if (atual.estado === 'OFFLINE') return atual;
      return {
        estado: ehRedeDegradada(evento.dicas) ? 'DEGRADED' : 'ONLINE',
        falhas: atual.falhas,
      };
    }

    default:
      // `evento` e uma uniao fechada e o `switch` a esgota; este ramo existe so para o
      // `noImplicitReturns`. Um `never` nomeado aqui viraria variavel nao usada.
      return atual;
  }
}

/**
 * `PWA-020` — nenhuma tela pode ficar em *skeleton* infinito por falha de rede.
 *
 * "Toda query tem timeout de 12 s (8 s em `DEGRADED` ja com *retry* configurado)". O numero
 * vive aqui, e nao em cada tela, porque uma tela que escolhesse o proprio timeout seria uma
 * segunda politica — e a que faltasse ficaria em skeleton para sempre.
 */
export function timeoutDeQueryMs(estado: EstadoDeConexao): number {
  return estado === 'DEGRADED' ? 8_000 : 12_000;
}
